// src/hooks/charts/useChartRenderDependencies.ts
import { shallowEqual, useSelector } from "react-redux";

import {
  hoverDataPointAtom,
  hoverLegendGroupAtom,
  hoverLegendIdAtom,
  hoverLegendIsMultiProductChartAtom,
  hoverLegendItemAtom,
  hoverLegendItemUnfilteredAtom
} from "atoms/interaction";
import { useStore } from "jotai";
import { RootState } from "store/rootReducer";
import {
  selectAttentionWells,
  selectFacilityLegendItems,
  selectSelectedWells,
  selectTxnId,
  selectWellLegendItems,
  useMapStore
} from "stores/map";

import { EntityKind } from "models/entityKind";

/**
 * Redux selector that extracts only the fields that drive a local visual
 * re-render against already-fetched data.
 *
 * Phase 2 of STATE_MANAGEMENT_MIGRATION_PLAN.md — Step 2.2.
 *
 * A change to any field in this selector means the chart must re-render
 * visually (e.g. color change, selection highlight) but does NOT need to
 * refetch data from the server. Fields that trigger a new API request belong
 * in `selectQueryDeps` (see useChartQueryDependencies.ts).
 */
const selectRenderDeps = (state: RootState, entityKind: EntityKind) => {
  const isWell = entityKind === EntityKind.Well;

  return {
    normalizeTypeWell: state.app.normalizeTypeWell,
    activeColorPalette: isWell
      ? state.userSetting.activeColorPalette
      : state.userSetting.activeFacilityColorPalette,
    colorPalettes: state.userSetting.colorPalettes,
    selectedGroups: state.groupBy.selectedGroups,
    selectedFacilityGroups: state.groupBy.selectedFacilityGroups
  };
};

/**
 * Hook that subscribes to render-driving chart dependencies (Redux) and
 * composes ephemeral hover state from Jotai atoms.
 *
 * Hover fields are read as snapshots via `store.get()` — they do NOT create
 * subscriptions. For reactive hover updates, subscribe at the Jotai store
 * level independently (see `HoverHighlightHandler` in `EChartWrapper.tsx`).
 *
 * Use this hook in rendering/highlighting paths (e.g. `useChartHighlights`,
 * `useChartSelections`) where a change should trigger a visual update but
 * NOT a new API request.
 *
 * @param entityKind - Well or Facility; selects the correct groupBy/color slices.
 */
export const useChartRenderDependencies = (entityKind: EntityKind) => {
  // Single Redux subscription for stable render-driving state (non-map).
  const reduxDeps = useSelector(
    (state: RootState) => selectRenderDeps(state, entityKind),
    shallowEqual
  );

  // Map state sourced from Zustand — avoids Redux for migrated map slice.
  const selectedWells = useMapStore(selectSelectedWells);
  const attentionWells = useMapStore(selectAttentionWells);
  const txnId = useMapStore(selectTxnId);
  const focusLegendItems = useMapStore(selectWellLegendItems);
  const facilityFocusLegendItems = useMapStore(selectFacilityLegendItems);

  // Hover fields: snapshot reads via store.get() — NO subscription.
  // This prevents every EChartsWrapper (and its 16 hooks) from re-rendering
  // on pointer-frequency hover changes. Hooks that need to react to hover
  // changes should subscribe at the store level independently
  // (see HoverHighlightHandler in EChartWrapper.tsx).
  const store = useStore();
  const hoverLegendItem = store.get(hoverLegendItemAtom);
  const hoverLegendGroup = store.get(hoverLegendGroupAtom);
  const hoverLegendId = store.get(hoverLegendIdAtom);
  const hoverLegendItemUnfiltered = store.get(hoverLegendItemUnfilteredAtom);
  const hoverLegendIsMultiProductChart = store.get(hoverLegendIsMultiProductChartAtom);
  const hoverDataPoint = store.get(hoverDataPointAtom);

  return {
    ...reduxDeps,
    selectedWells,
    attentionWells,
    txnId,
    focusLegendItems,
    facilityFocusLegendItems,
    hoverLegendItem,
    hoverLegendGroup,
    hoverLegendId,
    hoverLegendItemUnfiltered,
    hoverLegendIsMultiProductChart,
    hoverDataPoint
  };
};

export type ChartRenderDependencies = ReturnType<typeof useChartRenderDependencies>;

/**
 * Default / empty value for render dependencies.
 * Used by test utilities to provide a base mock value.
 */
export const DEFAULT_RENDER_DEPENDENCIES: ChartRenderDependencies = {
  // --- Redux render dependencies ---
  normalizeTypeWell: false,
  activeColorPalette: undefined,
  colorPalettes: undefined,
  selectedGroups: [],
  selectedFacilityGroups: [],
  selectedWells: undefined,
  attentionWells: [],
  txnId: undefined,
  focusLegendItems: undefined,
  facilityFocusLegendItems: undefined,

  // --- Jotai hover snapshots ---
  hoverLegendItem: "",
  hoverLegendGroup: "",
  hoverLegendId: "",
  hoverLegendItemUnfiltered: "",
  hoverLegendIsMultiProductChart: false,
  hoverDataPoint: []
};
